/**
 * PWA Installation Page
 * 
 * Shows installation instructions for PWA.
 * User will need to login separately via magic link after installing.
 * 
 * This is simpler and more secure than transferring session tokens via URL.
 */

import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getTelegram, isTelegramMiniApp } from "../../lib/telegram.ts";
import { useAuth } from "../../lib/auth.tsx";
import { getStoredTokens } from "../../lib/storage.ts";

export type DeviceType = "ios" | "android" | "desktop";

function detectDevice(): DeviceType {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

const INSTRUCTIONS: Record<DeviceType, { title: string; steps: string[] }> = {
  ios: {
    title: "iPhone / iPad",
    steps: [
      "Нажмите «Поделиться» (квадрат со стрелкой вверх).",
      "Выберите «На экран Домой» (или Add to Home Screen).",
      "Нажмите «Добавить» в правом верхнем углу.",
    ],
  },
  android: {
    title: "Android",
    steps: [
      "Откройте меню браузера (три точки ⋮).",
      "Выберите «Установить приложение» или «Добавить на главный экран».",
      "Подтвердите установку.",
    ],
  },
  desktop: {
    title: "Компьютер",
    steps: [
      "В адресной строке нажмите значок установки (⊕ или компьютер).",
      "Либо меню (⋮) → «Установить One Church».",
    ],
  },
};

// BeforeInstallPromptEvent type
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallInstructionPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const tg = getTelegram();
  const [device, setDevice] = useState<DeviceType>(() => detectDevice());
  const content = useMemo(() => INSTRUCTIONS[device], [device]);
  const [showContent, setShowContent] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true);

  // Capture install prompt for automatic installation
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Mini App should never be on /install — redirect to home immediately
  useEffect(() => {
    if (isTelegramMiniApp()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // PWA opened from home screen on /install — redirect if already has session
  useEffect(() => {
    // Mini App handled above
    if (isTelegramMiniApp()) return;

    const isDirectLaunch = !document.referrer || document.referrer === "";
    const hasTokens = !!getStoredTokens();

    // Redirect only if this is already-installed PWA with session
    if (isStandalone && isDirectLaunch && hasTokens) {
      navigate("/", { replace: true });
      return;
    }
    setShowContent(true);
  }, [navigate, isStandalone]);

  useEffect(() => {
    if (!tg || !showContent) return;
    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, showContent]);

  // Handle install button click (uses native prompt if available)
  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          // PWA installed!
        }
        setDeferredPrompt(null);
      } catch {
        setDeferredPrompt(null);
      }
    }
  };

  // Empty screen before redirect check completes (prevents flash)
  if (!showContent) {
    return (
      <div
        className="relative z-10 h-[100dvh] bg-[var(--color-bg)]"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        aria-hidden
      />
    );
  }

  return (
    <div
      className="relative z-10 flex flex-col h-[100dvh] overflow-hidden bg-[var(--color-bg)]"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header: logo, name */}
      <div className="shrink-0 w-full max-w-xl mx-auto px-3 py-3 xs:px-4 xs:py-4">
        <div className="flex items-center gap-2 xs:gap-3 mb-2 xs:mb-3">
          {!isTelegramMiniApp() && (
            <button
              type="button"
              onClick={() => navigate("/")}
              className="shrink-0 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              ← На главную
            </button>
          )}
          <img
            src="/logo.png"
            alt=""
            className="w-10 h-10 xs:w-12 xs:h-12 rounded-xl object-contain shrink-0"
          />
          <div className="min-w-0">
            <h1 className="text-base xs:text-lg font-semibold text-slate-900 truncate">
              One Church
            </h1>
            <p className="text-xs text-slate-500 truncate">
              евангельская церковь
            </p>
          </div>
        </div>

        {/* Auth status / login prompt */}
        {isAuthenticated ? (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-800 text-sm font-medium text-center">
              ✓ Вы авторизованы
            </p>
            <p className="text-green-700 text-xs text-center mt-1">
              Установите приложение по инструкции ниже
            </p>
          </div>
        ) : (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-blue-800 text-sm font-medium text-center">
              Войдите через email
            </p>
            <p className="text-blue-700 text-xs text-center mt-1 mb-2">
              После установки приложения войдите по email и паролю
            </p>
            <Link
              to="/login"
              className="block w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors text-center"
            >
              Войти
            </Link>
          </div>
        )}

        {/* Native install button (Chrome/Edge) */}
        {deferredPrompt && (
          <button
            type="button"
            onClick={handleInstall}
            className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-white transition-all active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span className="text-lg">⊕</span>
            <span>Установить приложение</span>
          </button>
        )}

        {/* Device selector (only show if no native prompt) */}
        {!deferredPrompt && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-2 xs:mb-3">
            {(["ios", "android", "desktop"] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDevice(d)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors shrink-0 ${
                  device === d
                    ? "bg-[var(--color-accent)] text-white"
                    : "bg-slate-200/80 text-slate-600"
                }`}
              >
                {d === "ios" ? "iPhone" : d === "android" ? "Android" : "ПК"}
              </button>
            ))}
          </div>
        )}

        {/* Manual instructions (show if no native prompt or as fallback) */}
        {!deferredPrompt && (
          <ol className="space-y-1 xs:space-y-1.5">
            {content.steps.map((step, i) => (
              <li key={i} className="flex gap-2 text-xs xs:text-sm text-slate-700">
                <span className="flex-shrink-0 w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-[var(--color-accent)] text-white font-semibold flex items-center justify-center text-[10px] xs:text-xs">
                  {i + 1}
                </span>
                <span className="pt-0.5 leading-tight">{step}</span>
              </li>
            ))}
          </ol>
        )}

        {/* Important note for Android */}
        {device === "android" && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 text-xs text-center">
              <strong>Важно (Android):</strong> Установите приложение <strong>прямо из этого браузера</strong>, не закрывая его. 
              Если PWA уже установлена — удалите её и установите заново.
            </p>
          </div>
        )}
        
        {/* Important note for iOS */}
        {device === "ios" && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-xs text-center">
              <strong>iOS:</strong> Откройте эту страницу в Safari, затем добавьте на главный экран.
            </p>
          </div>
        )}
      </div>

      {/* GIF — remaining space, full size (object-contain) */}
      {device === "ios" && !deferredPrompt && (
        <div className="flex-1 min-h-0 flex items-center justify-center px-2 pb-2">
          <img
            src="/save-app.gif"
            alt=""
            className="max-h-full max-w-full w-auto object-contain rounded-lg border border-slate-200 shadow-sm"
          />
        </div>
      )}
    </div>
  );
}
